/**
 * DashboardPage - Página Principal do Dashboard
 * 
 * Componente de página que compõe todas as seções do dashboard:
 * - Header
 * - Seção de KPIs
 * - Seção de Gráficos
 * - Seção de Alertas
 * 
 * Este componente é uma composição pura de componentes filhos,
 * sem lógica de negócio. Todo o gerenciamento de estado e dados
 * é delegado aos hooks e componentes filhos.
 * 
 * @module pages/Dashboard/DashboardPage
 */

import React from 'react';
import DashboardErrorBoundary from '../../components/Dashboard/DashboardErrorBoundary';
import SkipLinks from '../../components/Common/SkipLinks';

// Hooks de Dados
import { useDashboard } from '../../hooks/useDashboard';
import { useTaskDistribution } from '../../hooks/data/useTaskDistribution';
import { useWorkloadByProfessional } from '../../hooks/data/useWorkloadByProfessional';
import { useEquipmentStatusSummary } from '../../hooks/data/useEquipmentStatusSummary';
import { usePontoDesvios } from '../../hooks/data/usePontoDesvios';
import { useEstoqueAlertas } from '../../hooks/data/useEstoqueAlertas';
import { useDashboardPreferences } from '../../hooks/useDashboardPreferences';
import { useRealtimeData } from '../../hooks/useRealtimeData';

// Componentes de Dashboard
import { DashboardHeader } from '../../components/Dashboard/DashboardHeader';
import { KPISection } from '../../components/Dashboard/KPISection';
import { ChartsSection } from '../../components/Dashboard/ChartsSection';
import { AlertsSection } from '../../components/Dashboard/AlertsSection';

/**
 * Componente de Página do Dashboard
 * 
 * Renderiza todas as seções do dashboard usando composição de componentes.
 * Não contém lógica de negócio - apenas orquestra a renderização dos componentes filhos.
 * 
 * @returns {JSX.Element} Componente de página do dashboard
 */
const DashboardPage = () => {
    // Hook de preferências do dashboard
    const {
        preferences,
        setPeriod,
        toggleKPI,
        resetPreferences,
        setRealtimeConfig,
    } = useDashboardPreferences();

    // Hook de tempo real
    const realtimeData = useRealtimeData({
        enabled: preferences.realtimeEnabled || false,
        interval: preferences.realtimeInterval || 30000,
        useSSE: false, // TODO: Ativar quando backend suportar SSE
    });

    // Wrapper para toggleRealtime que também atualiza preferências
    const handleRealtimeToggle = React.useCallback(() => {
        const newState = !realtimeData.isRealtimeEnabled;
        realtimeData.toggleRealtime();
        setRealtimeConfig(newState);
    }, [realtimeData, setRealtimeConfig]);

    // Consumo dos Hooks de Dados
    // Cada hook gerencia seu próprio estado de loading/error
    // TODO: Passar período para os hooks quando backend suportar
    const { summary, isLoading: isLoadingSummary, error: errorSummary } = useDashboard();
    const { data: taskDistribution, isLoading: isLoadingDistribution, error: errorDistribution } = useTaskDistribution();
    const { data: workloadData, isLoading: isLoadingWorkload, error: errorWorkload } = useWorkloadByProfessional();
    const { data: equipmentStatusData, isLoading: isLoadingEquipment, error: errorEquipment } = useEquipmentStatusSummary();
    const { data: pontoDesviosData, isLoading: isLoadingPontoDesvios, error: errorPontoDesvios } = usePontoDesvios();
    const { data: estoqueAlertasData, isLoading: isLoadingEstoqueAlertas, error: errorEstoqueAlertas } = useEstoqueAlertas();

    return (
        <DashboardErrorBoundary section="Dashboard">
            {/* Skip Links para acessibilidade */}
            <SkipLinks />
            
            <main 
                id="dashboard-page"
                className="w-full min-w-0 p-3 sm:p-4 md:p-6 lg:p-8" 
                data-testid="dashboard-page"
                role="main"
                aria-label="Dashboard de Gestão"
            >
                {/* Header do Dashboard */}
                <DashboardHeader
                    period={preferences.period}
                    onPeriodChange={setPeriod}
                    visibleKPIs={preferences.visibleKPIs}
                    onKPIToggle={toggleKPI}
                    onResetPreferences={resetPreferences}
                    showFilters={true}
                    dashboardData={{
                        summary,
                        taskDistribution,
                        workload: workloadData,
                        equipmentStatus: equipmentStatusData,
                        pontoDesvios: pontoDesviosData,
                        estoqueAlertas: estoqueAlertasData,
                    }}
                    showExport={true}
                    exportOptions={{
                        period: preferences.period,
                        visibleKPIs: preferences.visibleKPIs,
                        organizationName: 'Organização', // TODO: Buscar do contexto de autenticação
                    }}
                    realtimeConfig={{
                        isRealtimeEnabled: realtimeData.isRealtimeEnabled,
                        isConnected: realtimeData.isConnected,
                        lastUpdate: realtimeData.lastUpdate,
                    }}
                    onRealtimeToggle={handleRealtimeToggle}
                    showRealtime={true}
                />

                {/* Seção de KPIs */}
                <KPISection
                    summary={summary}
                    isLoading={isLoadingSummary}
                    error={errorSummary}
                    showByDefault={true}
                    showToggle={true}
                    visibleKPIs={preferences.visibleKPIs}
                />

                {/* Seção de Gráficos */}
                <ChartsSection
                    taskDistribution={taskDistribution}
                    isLoadingTaskDistribution={isLoadingDistribution}
                    errorTaskDistribution={errorDistribution}
                    equipmentStatus={equipmentStatusData}
                    isLoadingEquipmentStatus={isLoadingEquipment}
                    errorEquipmentStatus={errorEquipment}
                    workload={workloadData}
                    isLoadingWorkload={isLoadingWorkload}
                    errorWorkload={errorWorkload}
                />

                {/* Seção de Alertas */}
                <AlertsSection
                    pontoDesvios={pontoDesviosData}
                    isLoadingPontoDesvios={isLoadingPontoDesvios}
                    errorPontoDesvios={errorPontoDesvios}
                    estoqueAlertas={estoqueAlertasData}
                    isLoadingEstoqueAlertas={isLoadingEstoqueAlertas}
                    errorEstoqueAlertas={errorEstoqueAlertas}
                />
            </main>
        </DashboardErrorBoundary>
    );
};

export default DashboardPage;
